import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/site/PageHeader";
import { SiteFooter } from "@/components/site/FinalCTA";
import AuthDialog from "@/components/site/AuthDialog";
import { useAuth } from "@/context/AuthContext";
import { cardsApi, portraitsApi } from "@/lib/cardsApi";
import { formatApiError } from "@/lib/api";
import { CARDS } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

function errMsg(e) {
  return formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong.";
}

const TOP_LIMIT = 60;
const BOTTOM_LIMIT = 220;

export default function CardStudioPage() {
  const { user, ready } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [occasions, setOccasions] = useState([]);
  const [portraits, setPortraits] = useState([]);
  const [samples, setSamples] = useState([]);

  const [state, setState] = useState({
    occasion: "in_memoriam",
    top_text: "",
    bottom_text: "",
    person_name: "",
    dates: "",
    portrait_url: "",
    format: "portrait",
  });

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(null); // "top" | "bottom" | null
  const [variantModal, setVariantModal] = useState({ open: false, position: null, variants: [] });

  const [saveBusy, setSaveBusy] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const uploadRef = useRef(null);
  const debounceRef = useRef(null);

  // ---- data ----
  useEffect(() => {
    (async () => {
      try {
        const data = await cardsApi.occasions();
        setOccasions(data.occasions);
      } catch (e) { console.error(e); }
      try {
        const s = await cardsApi.samples();
        setSamples(s.samples);
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const data = await portraitsApi.list();
        setPortraits(data.portraits);
      } catch (e) { console.error(e); }
    })();
  }, [user]);

  // ---- default headline when switching occasion ----
  const activeOccasion = useMemo(
    () => occasions.find((o) => o.id === state.occasion) || null,
    [occasions, state.occasion],
  );

  useEffect(() => {
    if (activeOccasion && !state.top_text) {
      setState((s) => ({ ...s, top_text: activeOccasion.headline_default }));
    }
    // intentionally skip state.top_text — we only auto-fill when empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOccasion?.id]);

  // ---- debounced preview refresh ----
  const buildPayload = useCallback(() => ({
    occasion: state.occasion,
    portrait_url: state.portrait_url || null,
    top_text: state.top_text.slice(0, TOP_LIMIT),
    bottom_text: state.bottom_text.slice(0, BOTTOM_LIMIT),
    person_name: state.person_name || null,
    dates: state.dates || null,
    format: state.format,
  }), [state]);

  const refreshPreview = useCallback(async () => {
    if (!user) return;
    setPreviewLoading(true);
    try {
      const url = await cardsApi.composeBlobUrl(buildPayload());
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPreviewLoading(false);
    }
  }, [user, buildPayload]);

  useEffect(() => {
    if (!user) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(refreshPreview, 550);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [user, state, refreshPreview]);

  // Cleanup blob URL on unmount
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  // ---- handlers ----
  const set = (k) => (e) => setState((s) => ({ ...s, [k]: typeof e === "string" ? e : e.target.value }));

  async function aiSuggest(position) {
    setAiBusy(position);
    try {
      const data = await cardsApi.aiSuggest({
        occasion: state.occasion,
        position,
        person_name: state.person_name || null,
        context: null,
      });
      if (!data.variants?.length) {
        toast.error("The AI didn't return any suggestions. Try again in a moment.");
        return;
      }
      setVariantModal({ open: true, position, variants: data.variants });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAiBusy(null);
    }
  }

  function pickVariant(text) {
    if (variantModal.position === "top") {
      setState((s) => ({ ...s, top_text: text.slice(0, TOP_LIMIT) }));
    } else {
      setState((s) => ({ ...s, bottom_text: text.slice(0, BOTTOM_LIMIT) }));
    }
    setVariantModal({ open: false, position: null, variants: [] });
  }

  async function handlePortraitUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const doc = await portraitsApi.upload(file);
      setPortraits((cur) => [doc, ...cur]);
      setState((s) => ({ ...s, portrait_url: doc.url }));
      toast.success("Portrait uploaded and selected.");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      if (uploadRef.current) uploadRef.current.value = "";
    }
  }

  async function handleSaveToMyCards({ publishToFeed = false } = {}) {
    setSaveBusy(true);
    try {
      const payload = {
        ...buildPayload(),
        title: (state.person_name || state.top_text || "Untitled").slice(0, 120),
        publish_to: publishToFeed ? state.occasion : null,
      };
      await cardsApi.saveComposed(payload);
      toast.success(publishToFeed
        ? `Saved & published to the ${activeOccasion?.label || state.occasion} feed.`
        : "Saved to your cards.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaveBusy(false);
    }
  }

  async function handleDownload() {
    try {
      const blob = await cardsApi.composeBlob(buildPayload());
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${state.occasion}-${(state.person_name || "card").replace(/\s+/g, "-").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }

  function applySample(sample) {
    setState((s) => ({
      ...s,
      occasion: sample.occasion,
      top_text: sample.top_text || "",
      bottom_text: sample.bottom_text || "",
      person_name: sample.person_name || "",
      dates: sample.dates || "",
      portrait_url: sample.portrait_url || "",
      format: sample.format || "portrait",
    }));
    toast.success(`Template loaded: ${sample.title}`);
    // scroll to the form
    setTimeout(() => {
      document.getElementById("card-form-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  // ---- render (anonymous) ----
  if (!ready) return <div className="min-h-screen bg-ivory" />;

  if (!user) {
    return (
      <div data-testid={CARDS.page} className="min-h-screen bg-ivory text-ink">
        <PageHeader
          eyebrow="Personal Message Cards"
          title="A keepsake"
          italic="for every"
          tail="passage of a life."
          subtitle="Create a personal message card using images, text and AI suggestions — birthdays, anniversaries, graduations, memorials, and every occasion that deserves to be remembered."
        />
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-20">
          <div data-testid={CARDS.loginPrompt} className="rounded-2xl border border-ivory-300 bg-white/60 p-10 text-center">
            <p className="font-serif text-2xl text-ink">Sign in to open the card studio.</p>
            <p className="mt-2 text-ink-50">Your cards are private to you until you choose to share them.</p>
            <button
              onClick={() => setAuthOpen(true)}
              className="mt-8 rounded-full bg-saffron text-ivory px-6 py-3 font-medium hover:bg-saffron-deep transition"
            >
              Sign in or register
            </button>
          </div>
          <SamplesStrip samples={samples} onUse={() => setAuthOpen(true)} anonymous />
        </div>
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialTab="register" />
        <SiteFooter />
      </div>
    );
  }

  // ---- render (authenticated) ----
  return (
    <div data-testid={CARDS.page} className="min-h-screen bg-ivory text-ink">
      <PageHeader
        eyebrow="Personal Message Cards"
        title="A keepsake"
        italic="for every"
        tail="passage of a life."
        subtitle="Pick an occasion, upload a photograph, write the words. Use AI-suggested variants if you'd like a starting point. Your card is composed live on the right — download it when it feels right."
      />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-10 pb-24">
        <SamplesStrip samples={samples} onUse={applySample} />

        <div id="card-form-anchor" className="grid grid-cols-12 gap-8 mt-16">
        {/* LEFT: form */}
        <div className="col-span-12 lg:col-span-7">
          {/* Occasion */}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-ink-50">Occasion</Label>
            <div
              data-testid={CARDS.occasionGrid}
              className="mt-3 flex flex-wrap gap-2"
            >
              {occasions.map((o) => (
                <button
                  key={o.id}
                  data-testid={CARDS.occasionPill(o.id)}
                  onClick={() => setState((s) => ({ ...s, occasion: o.id }))}
                  className={`px-4 py-2 rounded-full border text-sm transition ${
                    state.occasion === o.id
                      ? "border-saffron bg-saffron text-ivory"
                      : "border-ivory-300 bg-white/60 text-ink-50 hover:text-ink hover:border-ink-50/40"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Portrait picker */}
          <div className="mt-8">
            <Label className="text-[10px] uppercase tracking-[0.22em] text-ink-50">Portrait</Label>
            <div className="mt-3">
              <input
                ref={uploadRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePortraitUpload}
                className="hidden"
                data-testid={CARDS.portraitUpload}
              />
              <div
                data-testid={CARDS.portraitPicker}
                className="flex flex-wrap gap-3 items-center"
              >
                <button
                  onClick={() => uploadRef.current?.click()}
                  className="w-20 h-20 rounded-2xl border border-dashed border-ink-50/40 text-ink-50 hover:border-saffron hover:text-saffron flex items-center justify-center transition"
                  title="Upload a portrait"
                >
                  <span className="text-2xl">+</span>
                </button>
                {portraits.map((p) => {
                  const selected = state.portrait_url === p.url;
                  return (
                    <button
                      key={p.id}
                      data-testid={CARDS.portraitOption(p.id)}
                      onClick={() => setState((s) => ({ ...s, portrait_url: s.portrait_url === p.url ? "" : p.url }))}
                      className={`relative w-20 h-20 rounded-2xl overflow-hidden border-2 transition ${
                        selected ? "border-saffron" : "border-ivory-300 hover:border-ink-50/40"
                      }`}
                    >
                      <img
                        src={portraitsApi.publicUrl(p.url)}
                        alt={p.original_name}
                        className="w-full h-full object-cover"
                      />
                      {selected && (
                        <span className="absolute inset-0 border-4 border-saffron rounded-2xl pointer-events-none" />
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-ink-50">
                Pick a portrait to include in the card, or upload a new one.
                <Link to="/my-portraits" className="ml-2 text-tulsi hover:text-saffron transition">
                  Manage your portraits →
                </Link>
              </p>
            </div>
          </div>

          {/* Person + dates */}
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-ink-50">Person's name</Label>
              <Input
                data-testid={CARDS.personName}
                value={state.person_name}
                onChange={set("person_name")}
                placeholder="e.g. Sabi Joseph Parayil"
                className="mt-2 h-11 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-ink-50">Dates or subtitle</Label>
              <Input
                data-testid={CARDS.dates}
                value={state.dates}
                onChange={set("dates")}
                placeholder="e.g. Oct 14, 1962 — Aug 03, 2026"
                className="mt-2 h-11 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
              />
            </div>
          </div>

          {/* Top text */}
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-[0.22em] text-ink-50">
                Top text — the headline
              </Label>
              <div className="flex items-center gap-3">
                <span className={`text-xs ${state.top_text.length > TOP_LIMIT ? "text-rust" : "text-ink-50"}`}>
                  {state.top_text.length}/{TOP_LIMIT}
                </span>
                <button
                  data-testid={CARDS.aiSuggestTop}
                  onClick={() => aiSuggest("top")}
                  disabled={aiBusy === "top"}
                  className="text-xs uppercase tracking-[0.16em] text-saffron hover:text-saffron-deep disabled:opacity-50 transition"
                >
                  {aiBusy === "top" ? "Asking AI…" : "AI suggest ▸"}
                </button>
              </div>
            </div>
            <Input
              data-testid={CARDS.topText}
              value={state.top_text}
              maxLength={TOP_LIMIT}
              onChange={set("top_text")}
              placeholder="e.g. Happy Birthday, Samantha"
              className="mt-2 h-11 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
            />
          </div>

          {/* Bottom text */}
          <div className="mt-6">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-[0.22em] text-ink-50">
                Bottom text — the message
              </Label>
              <div className="flex items-center gap-3">
                <span className={`text-xs ${state.bottom_text.length > BOTTOM_LIMIT ? "text-rust" : "text-ink-50"}`}>
                  {state.bottom_text.length}/{BOTTOM_LIMIT}
                </span>
                <button
                  data-testid={CARDS.aiSuggestBottom}
                  onClick={() => aiSuggest("bottom")}
                  disabled={aiBusy === "bottom"}
                  className="text-xs uppercase tracking-[0.16em] text-saffron hover:text-saffron-deep disabled:opacity-50 transition"
                >
                  {aiBusy === "bottom" ? "Asking AI…" : "AI suggest ▸"}
                </button>
              </div>
            </div>
            <Textarea
              data-testid={CARDS.bottomText}
              value={state.bottom_text}
              maxLength={BOTTOM_LIMIT}
              onChange={set("bottom_text")}
              rows={4}
              placeholder="Two or three heartfelt sentences to close the card."
              className="mt-2 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
            />
          </div>

          {/* Actions */}
          <div className="mt-8 rounded-2xl border border-ivory-300 bg-white/70 p-5">
            <div className="flex flex-wrap items-center gap-3">
              <Label className="text-[10px] uppercase tracking-[0.22em] text-ink-50">Format</Label>
              <select
                data-testid={CARDS.format}
                value={state.format}
                onChange={set("format")}
                className="h-10 rounded-full border border-ivory-300 bg-ivory pl-4 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-tulsi"
              >
                <option value="portrait">Portrait · 1024 × 1536</option>
                <option value="landscape">Landscape · 1600 × 1067</option>
              </select>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <button
                  data-testid={CARDS.download}
                  onClick={handleDownload}
                  className="h-10 rounded-full border border-ink-50/30 text-ink px-4 text-sm hover:border-ink transition"
                >
                  Download PNG
                </button>
                <button
                  onClick={() => handleSaveToMyCards({ publishToFeed: false })}
                  disabled={saveBusy}
                  className="h-10 rounded-full bg-tulsi text-ivory px-4 text-sm font-medium hover:bg-tulsi-deep disabled:opacity-60 transition"
                >
                  {saveBusy ? "Saving…" : "Save to my cards"}
                </button>
                <button
                  data-testid={CARDS.saveBtn}
                  onClick={() => handleSaveToMyCards({ publishToFeed: true })}
                  disabled={saveBusy}
                  className="h-10 rounded-full bg-saffron text-ivory px-5 text-sm font-medium hover:bg-saffron-deep disabled:opacity-60 transition"
                >
                  {saveBusy ? "Publishing…" : `Publish to ${activeOccasion?.label || state.occasion}`}
                </button>
              </div>
            </div>
            <p className="mt-3 text-xs text-ink-50">
              Publishing puts your card on the public
              {" "}
              <Link to={`/celebrate/${state.occasion}`} className="text-tulsi hover:text-saffron transition">
                {activeOccasion?.label || state.occasion} feed
              </Link>
              . You can unpublish anytime from your
              {" "}
              <Link to="/my-cards" className="text-tulsi hover:text-saffron transition">my cards</Link>
              {" "}gallery.
            </p>
            <div className="mt-4 pt-4 border-t border-ivory-300 flex flex-wrap items-center gap-3">
              <span className="text-xs text-ink-50">Made a card elsewhere?</span>
              <button
                onClick={() => setUploadDialogOpen(true)}
                className="h-9 rounded-full border border-ivory-300 bg-white text-sm text-ink hover:border-saffron hover:text-saffron transition px-4"
              >
                Upload an existing card →
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT: preview */}
        <div className="col-span-12 lg:col-span-5">
          <div className="sticky top-32">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.22em] text-ink-50">Live preview</span>
              <button
                data-testid={CARDS.refreshPreview}
                onClick={refreshPreview}
                className="text-xs text-ink-50 hover:text-ink transition"
              >
                Refresh
              </button>
            </div>
            <div className="mt-3 rounded-2xl border border-ivory-300 bg-white/70 overflow-hidden">
              <div className="relative w-full" style={{ aspectRatio: state.format === "portrait" ? "1024/1536" : "1600/1067" }}>
                {previewUrl ? (
                  <img
                    data-testid={CARDS.preview}
                    src={previewUrl}
                    alt="Card preview"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-ink-50 text-sm">
                    Preview loading…
                  </div>
                )}
                {previewLoading && previewUrl && (
                  <div className="absolute inset-0 bg-ivory/40 flex items-center justify-center pointer-events-none">
                    <div className="h-2 w-24 bg-saffron/40 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full w-1/2 bg-saffron"
                        animate={{ x: ["-100%", "200%"] }}
                        transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <p className="mt-3 text-xs text-ink-50">
              This preview updates automatically as you edit. It's exactly what will download.
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Variant picker */}
      <AnimatePresence>
        {variantModal.open && (
          <VariantModal
            variants={variantModal.variants}
            onPick={pickVariant}
            onClose={() => setVariantModal({ open: false, position: null, variants: [] })}
          />
        )}
      </AnimatePresence>

      <UploadCardDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        occasions={occasions}
        defaultOccasion={state.occasion}
      />

      <SiteFooter />
    </div>
  );
}

function VariantModal({ variants, onPick, onClose }) {
  const TONE_META = {
    warm: { label: "Warm", note: "Heartfelt and familial" },
    formal: { label: "Formal", note: "Dignified and restrained" },
    poetic: { label: "Poetic", note: "Lightly literary" },
  };
  return (
    <motion.div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        data-testid={CARDS.variantPicker}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.98, y: 10 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="bg-ivory border border-ivory-300 rounded-2xl max-w-xl w-full p-6"
      >
        <div className="font-serif text-xl text-ink">Pick a tone</div>
        <p className="text-sm text-ink-50 mt-1">Choose one to insert into your card — you can edit it afterwards.</p>
        <div className="mt-5 space-y-3">
          {variants.map((v, i) => {
            const meta = TONE_META[v.tone] || { label: v.tone, note: "" };
            return (
              <button
                key={i}
                data-testid={CARDS.variantOption(i)}
                onClick={() => onPick(v.text)}
                className="w-full text-left rounded-xl border border-ivory-300 bg-white/70 hover:border-saffron hover:bg-saffron/5 transition p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-[0.16em] text-saffron">{meta.label}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-ink-50">{meta.note}</div>
                </div>
                <p className="mt-2 font-serif text-ink leading-relaxed">{v.text}</p>
              </button>
            );
          })}
        </div>
        <div className="mt-5 text-right">
          <button onClick={onClose} className="text-sm text-ink-50 hover:text-ink transition">Cancel</button>
        </div>
      </motion.div>
    </motion.div>
  );
}


function SamplesStrip({ samples, onUse, anonymous = false }) {
  if (!samples?.length) return null;
  return (
    <div data-testid={CARDS.samplesStrip} className={anonymous ? "mt-12" : ""}>
      <div className="flex items-end justify-between">
        <div>
          <span className="text-[10px] uppercase tracking-[0.24em] text-saffron">Inspiration</span>
          <h3 className="mt-1 font-serif text-2xl md:text-3xl text-ink">
            {anonymous ? "See what you can make" : "Start from a template"}
          </h3>
        </div>
        {!anonymous && (
          <p className="hidden md:block text-xs text-ink-50 max-w-xs text-right">
            Click any card to load it into the studio — edit the wording, swap the portrait, download.
          </p>
        )}
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
        {samples.map((s, i) => (
          <motion.button
            key={i}
            data-testid={CARDS.sampleCard(i)}
            onClick={() => onUse(s, i)}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.03, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -4 }}
            className="group text-left rounded-2xl border border-ivory-300 bg-white/70 overflow-hidden hover:border-saffron hover:shadow-[0_20px_40px_-24px_rgba(232,106,51,0.35)] transition"
          >
            <div className="aspect-[1024/1536] overflow-hidden bg-ivory-100">
              <img
                src={cardsApi.sampleUrl(i)}
                alt={s.title}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
              />
            </div>
            <div className="px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.16em] text-saffron">
                {s.occasion.replace(/_/g, " ")}
              </div>
              <div className="mt-1 font-serif text-ink truncate">{s.title}</div>
              <div className="mt-2">
                <span
                  data-testid={CARDS.sampleApplyBtn(i)}
                  className="inline-flex items-center gap-1 text-xs text-tulsi group-hover:text-saffron transition"
                >
                  {anonymous ? "Sign in to use →" : "Use this template →"}
                </span>
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

function UploadCardDialog({ open, onClose, occasions, defaultOccasion }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [occasion, setOccasion] = useState(defaultOccasion || "in_memoriam");
  const [title, setTitle] = useState("");
  const [personName, setPersonName] = useState("");
  const [publishNow, setPublishNow] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null); setPreviewUrl(null); setTitle(""); setPersonName("");
      setOccasion(defaultOccasion || "in_memoriam"); setPublishNow(true);
    }
  }, [open, defaultOccasion]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  function pickFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error("Keep the image under 12 MB."); return; }
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  }

  async function submit() {
    if (!file) { toast.error("Pick an image first."); return; }
    setBusy(true);
    try {
      await cardsApi.uploadCard(file, {
        occasion,
        title: title || null,
        person_name: personName || null,
        publish_to: publishNow ? occasion : null,
      });
      const label = occasions.find((o) => o.id === occasion)?.label || occasion;
      toast.success(publishNow ? `Uploaded & published to ${label}.` : "Uploaded to your cards.");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;
  return (
    <motion.div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      onClick={onClose}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="bg-ivory border border-ivory-300 rounded-2xl max-w-lg w-full p-6"
      >
        <div className="font-serif text-xl text-ink">Upload an existing card</div>
        <p className="text-sm text-ink-50 mt-1">
          Cards you made in ChatGPT, Canva, or elsewhere. We'll host the image and optionally publish it to a feed.
        </p>

        <div className="mt-5 space-y-4">
          <div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={pickFile}
              data-testid={CARDS.uploadFile}
              className="block w-full text-sm text-ink-50 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-tulsi file:text-ivory hover:file:bg-tulsi-deep"
            />
          </div>
          {previewUrl && (
            <div className="rounded-xl overflow-hidden border border-ivory-300 bg-white/60 max-h-64 flex items-center justify-center">
              <img src={previewUrl} alt="Card preview" className="max-h-64 object-contain" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-ink-50">Occasion</Label>
              <select
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                className="mt-1.5 h-11 w-full rounded-md border border-ivory-300 bg-transparent px-3 text-sm focus:outline-none focus:ring-2 focus:ring-tulsi"
              >
                {occasions.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-ink-50">Person</Label>
              <Input
                value={personName}
                onChange={(e) => setPersonName(e.target.value)}
                placeholder="Optional"
                className="mt-1.5 h-11 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-ink-50">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. In loving memory of Achamma"
              className="mt-1.5 h-11 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-50">
            <input
              type="checkbox"
              checked={publishNow}
              onChange={(e) => setPublishNow(e.target.checked)}
              className="accent-saffron"
            />
            Publish to the {occasions.find((o) => o.id === occasion)?.label || occasion} feed straight away
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-full text-sm text-ink-50 hover:text-ink transition">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !file}
            data-testid={CARDS.uploadSubmit}
            className="h-10 rounded-full bg-saffron text-ivory px-5 text-sm font-medium hover:bg-saffron-deep disabled:opacity-60 transition"
          >
            {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}